"use client";

import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogCloseButton } from "@shared/src/components/ui/dialog";
import { Button } from "@shared/src/components/ui/button";
import { IoCopy } from "react-icons/io5";
import { FaEthereum } from "react-icons/fa";
import { SiBinance, SiPolygon, SiTether } from "react-icons/si";
import { FaArrowUpRightFromSquare, FaPowerOff } from "react-icons/fa6";
import { Account, useAuthStore } from "@shared/src/stores/useAuthStore";
import { copyToClipboard, shortenAddress } from "@shared/src/utils/helpers";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { NetworkSelector } from "@shared/src/components/common/form-components/NetworkSelector";
import { NetworkAvatar } from "@shared/src/components/common/avatars/NetworkAvatar";
import { useModalStore } from "@shared/src/stores/useModalStore";
import { ixToast } from "@shared/src/utils/ixToast";
import { useHostStore } from "@shared/src/stores/useHostStore";

interface UserAccountHeaderProps {
  account: Account;
  onOpenChange: (open: boolean) => void;
}

export function AccountCard({ account, onOpenChange }: UserAccountHeaderProps) {
  const disconnectAccount = useAuthStore(state => state.disconnectAccount);
  const activeChain = useHostStore(state => state.activeChain);

  function handleDisconnect() {
    disconnectAccount(account);
    onOpenChange(false);
    //router.refresh();
  }

  return (
    <div className="flex items-center justify-between bg-surfaces-bg-secondary rounded-2xl p-6">
      <div className="flex items-center gap-4">
        <NetworkAvatar connectorIcon={account.connectorIcon} />
      </div>
      <div className="flex items-center gap-2">
        <Button variant="surface-primary" size="icon" radius="full" asChild>
          <Link
            href={activeChain?.blockExplorers?.default.url + "/address/" + account.address}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaArrowUpRightFromSquare size={16} />
          </Link>
        </Button>
        <Button
          onClick={() => copyToClipboard(account.address, () => ixToast.success("Address copied to clipboard"))}
          variant="surface-primary"
          size="icon"
          radius="full"
        >
          <IoCopy size={16} />
        </Button>
        <Button onClick={handleDisconnect} variant="surface-primary" size="icon" radius="full">
          <FaPowerOff size={16} />
        </Button>
      </div>
    </div>
  );
}

interface UserAccountModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const BALANCES = [
  {
    icon: <FaEthereum size={24} />,
    name: "ETH",
    subtext: "ETH",
    amount: "00094.00 ETH",
    value: "$1,000.00",
  },
  {
    icon: <SiTether size={24} />,
    name: "USDT",
    subtext: "USDT",
    amount: "00094.00 USDT",
    value: "$1,000.00",
  },
  {
    icon: <SiPolygon size={24} />,
    name: "USDC",
    subtext: "USDC",
    amount: "00094.00 USDC",
    value: "$1,000.00",
  },
  {
    icon: <SiBinance size={24} />,
    name: "BTC",
    subtext: "Bitcoin",
    amount: "00094.00 BTC",
    value: "$1,000.00",
  },
];

export function UserAccountModal({ open, onOpenChange }: UserAccountModalProps) {
  const router = useRouter();
  const { signOut, isVerifying } = useAuthStore();
  const { connectedAccounts, setModalOpen, activeAccount } = useAuthStore();

  async function handleConnectAnotherWallet() {
    const modal = useModalStore.getState();
    modal.showModal({
      type: "info",
      title: "Connect Wallet",
      description: "You will be disconnected from the current wallet. Are you sure you want to continue?",
      leftButtonClick: async () => {
        modal.closeModal();
      },
      buttonClick: async () => {
        signOut();
        setModalOpen(true);
      },
    });
  }

  const handleSignOut = () => {
    signOut();
    onOpenChange(false);
    //router.refresh();
  };

  const handleExpression = () => {
    // Add the expression here
  };
  handleExpression();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size="lg"
        background="primary"
        padding="lg"
        position="center-mobile-bottom"
        hideCloseButton={true}
        height="80vh"
        layout="flex-col-overflow"
        className="space-y-4 gap-0 md:px-8 px-6 py-6 md:pb-6 h-auto"
      >
        <DialogTitle className="sr-only">User Account</DialogTitle>
        <DialogHeader className="shrink-0">
          <div className="flex items-center justify-between">
            <Button variant="tertiary" radius="full" size="md" onClick={handleConnectAnotherWallet}>
              Connect Another Wallet
            </Button>
            <DialogCloseButton onClose={() => onOpenChange(false)} />
          </div>
        </DialogHeader>

        {connectedAccounts.map(account => (
          <AccountCard key={account.address} account={account} onOpenChange={onOpenChange} />
        ))}

        <div className="flex-1 overflow-y-auto min-h-0 custom-scrollbar space-y-4">
          <div className="space-y-4">
            <p className="text-text-tertiary text-small font-bold">Current Network</p>
            <NetworkSelector modal />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
